import { LiveMessage, constructLiveMessageFromChatMessage, LiveImageStatus } from './liveMessage';
import { Character } from '@/app/lib/types/character';
import { ChatMessage } from '../../server/db/chat';
import {
  writeMessageToSessionAction,
  updateMessageInSessionAction,
  removeMessageFromSessionAction,
  createDefaultMessagesAction
} from '../../server/action/chatActions';

/**
 * LiveMessageGroup represents an array of LiveMessage objects
 * with operations for managing the group as a whole
 * @deprecated Use LiveMessageDataHub singleton instead
 */
export interface LiveMessageGroup {
  messages: LiveMessage[];
}

/**
 * Singleton class that manages LiveMessage data for the chat
 * Replaces the previous functional approach with a centralized state manager
 * Now includes database synchronization for all message operations
 */
export class LiveMessageDataHub {
  private static instance: LiveMessageDataHub;
  private messages: LiveMessage[] = [];
  private subscribers: Set<(messages: LiveMessage[]) => void> = new Set();
  private currentUserEmail: string | null = null;
  private currentAvatarId: string | null = null;

  private constructor() {}

  /**
   * Get the singleton instance of LiveMessageDataHub
   * @returns The singleton instance
   */
  public static getInstance(): LiveMessageDataHub {
    if (!LiveMessageDataHub.instance) {
      LiveMessageDataHub.instance = new LiveMessageDataHub();
    }
    return LiveMessageDataHub.instance;
  }

  /**
   * Set the current user and avatar context for database operations
   * @param userEmail The user's email
   * @param avatarId The avatar ID
   */
  public setContext(userEmail: string, avatarId: string): void {
    this.currentUserEmail = userEmail;
    this.currentAvatarId = avatarId;
  }

  /**
   * Subscribe to message changes
   * @param callback Function to call when messages change
   * @returns Unsubscribe function
   */
  public subscribe(callback: (messages: LiveMessage[]) => void): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Notify all subscribers of message changes
   */
  private notifySubscribers(): void {
    this.subscribers.forEach(callback => {
      try {
        callback([...this.messages]); // Send a copy to prevent external mutations
      } catch (error) {
        console.error('Error in LiveMessageDataHub subscriber:', error);
      }
    });
  }

  /**
   * Get current messages (returns a copy to prevent external mutations)
   * @returns Copy of current messages array
   */
  public getMessages(): LiveMessage[] {
    return [...this.messages];
  }

  /**
   * Initialize chat history for a character
   * If character has existing chat history, use that
   * If not, create a greeting message from the character
   * @param character The character to initialize chat history for
   * @param userEmail The user's email for database context
   * @param avatarId The avatar ID for database context
   */
  public async initChatHistory(character: Character, userEmail: string, avatarId: string): Promise<void> {
    // Set context if provided
    this.setContext(userEmail, avatarId);

    // Check if character has existing chat history
    const characterHasHistory = character.chatHistory && character.chatHistory.messages && character.chatHistory.messages.length > 0;
    if (characterHasHistory) {
      this.messages = character.chatHistory?.messages.map(constructLiveMessageFromChatMessage) || [];
    } else {
      await this.initDefaultHistory(character);
    }

    this.notifySubscribers();
  }

  /**
   * If character has existing chat history, use that
   * If not, create a greeting message from the character
   * @param character The character to initialize chat history for
   */
  public async initDefaultHistory(character: Character): Promise<void> {
    const greetingContent = character.metadata.opening_prompt ?? '';

    const defaultImageMessage: ChatMessage = {
      id: 'default_image', // use default_image for default image
      content: character.metadata.img_caption,
      role: 'assistant',
      sender_id: character.metadata.avatar_id,
      sender_name: character.metadata.avatar_name,
      created_at: new Date().toISOString(),
      imageUrl: character.metadata.image_uri || '',
    };

    const greetingMessage: ChatMessage = {
      id: 'default_greeting', // use default_greeting for default greeting
      content: greetingContent,
      role: 'assistant',
      sender_id: character.metadata.avatar_id,
      sender_name: character.metadata.avatar_name,
      created_at: new Date().toISOString(),
    };

    console.log("defaultImageMessage: ", defaultImageMessage);
    console.log("greetingMessage: ", greetingMessage);

    this.messages = [
      constructLiveMessageFromChatMessage(defaultImageMessage),
      constructLiveMessageFromChatMessage(greetingMessage)
    ];

    // Save default messages to database if we have context
    if (this.currentUserEmail && this.currentAvatarId) {
      try {
        const result = await createDefaultMessagesAction(
          this.currentAvatarId,
          character.metadata.avatar_name,
          character.metadata.image_uri || '',
          character.metadata.img_caption,
          greetingContent
        );
        if (result.success) {
          console.log('Saved default messages to database');
        } else {
          console.error('Error saving default messages to database:', result.message);
        }
      } catch (error) {
        console.error('Error saving default messages to database:', error);
      }
    }

    this.notifySubscribers();
  }

  /**
   * Clear all messages
   */
  public clearMessages(): void {
    this.messages = [];
    this.notifySubscribers();
  }

  /**
   * Add a LiveMessage to the hub and sync to database
   * @param liveMessage The LiveMessage to add
   */
  public async addMessage(liveMessage: LiveMessage): Promise<void> {
    this.messages = [...this.messages, liveMessage];

    // Sync to database if we have context
    if (this.currentUserEmail && this.currentAvatarId) {
      try {
        // Convert LiveMessage back to ChatMessage for database storage
        const chatMessage: ChatMessage = {
          id: liveMessage.id,
          content: liveMessage.content,
          role: liveMessage.role,
          sender_id: liveMessage.sender_id,
          sender_name: liveMessage.sender_name,
          created_at: liveMessage.created_at,
          imageUrl: liveMessage.imageUrl,
          model: (liveMessage as any).model,
          prompt_tokens: (liveMessage as any).prompt_tokens,
          out_tokens: (liveMessage as any).out_tokens,
          deleted: (liveMessage as any).deleted || false,
          filtered: (liveMessage as any).filtered || false,
          context: (liveMessage as any).context || -1,
        };

        const result = await writeMessageToSessionAction(this.currentAvatarId, chatMessage);
        if (!result.success) {
          console.error('Error saving message to database:', result.message);
        }
      } catch (error) {
        console.error('Error saving message to database:', error);
      }
    }

    this.notifySubscribers();
  }

  /**
   * Add a LiveMessage to the hub (frontend state only)
   * @param liveMessage The LiveMessage to add to frontend state
   */
  public addMessageFrontendOnly(liveMessage: LiveMessage): void {
    this.messages = [...this.messages, liveMessage];
    this.notifySubscribers();
  }

  /**
   * Add a LiveMessage to the database only (no frontend state update)
   * @param liveMessage The LiveMessage to add to database
   */
  public async addMessageDBOnly(liveMessage: LiveMessage): Promise<void> {
    // Sync to database if we have context
    if (this.currentUserEmail && this.currentAvatarId) {
      try {
        // Convert LiveMessage back to ChatMessage for database storage
        const chatMessage: ChatMessage = {
          id: liveMessage.id,
          content: liveMessage.content,
          role: liveMessage.role,
          sender_id: liveMessage.sender_id,
          sender_name: liveMessage.sender_name,
          created_at: liveMessage.created_at,
          imageUrl: liveMessage.imageUrl,
          model: (liveMessage as any).model,
          prompt_tokens: (liveMessage as any).prompt_tokens,
          out_tokens: (liveMessage as any).out_tokens,
          deleted: (liveMessage as any).deleted || false,
          filtered: (liveMessage as any).filtered || false,
          context: (liveMessage as any).context || -1,
        };

        const result = await writeMessageToSessionAction(this.currentAvatarId, chatMessage);
        if (!result.success) {
          console.error('Error saving message to database:', result.message);
        }
      } catch (error) {
        console.error('Error saving message to database:', error);
      }
    }
  }

  /**
   * Get the count of messages
   * @returns Number of messages
   */
  public getMessageCount(): number {
    return this.messages.length;
  }

  /**
   * Check if there are no messages
   * @returns True if no messages exist
   */
  public isEmpty(): boolean {
    return this.messages.length === 0;
  }

  /**
   * Update a specific LiveMessage by messageId, Overwritting the content and imageUrl
   * @param messageId The ID of the message to update
   * @param content The new content for the message
   * @param imageUrl Optional image URL
   * @param isStreaming Optional boolean to update the streaming status
   */
  public async updateMessage(messageId: string, content?: string, imageUrl?: string, isStreaming?: boolean): Promise<void> {
    this.messages = this.messages.map(msg => {
      if (msg.id === messageId) {
        const updatedMsg = {
          ...msg,
          ...(content !== undefined && { content }),
          ...(imageUrl !== undefined && { imageUrl }),
          ...(isStreaming !== undefined && { isStreaming })
        };

        // If imageUrl is being updated, also update the imageWithStatus.imageUri
        if (imageUrl !== undefined) {
          updatedMsg.imageWithStatus = {
            ...msg.imageWithStatus,
            imageUri: imageUrl,
            // Reset image data and status when imageUrl changes
            imageData: null,
            imageRawData: null,
            loadingStatus: imageUrl && imageUrl.trim() ? LiveImageStatus.LOADING : LiveImageStatus.NOT_LOADED
          };
        }

        return updatedMsg;
      }
      return msg;
    });

    // Sync to database if we have context and content is being updated (not just streaming status)
    if (this.currentUserEmail && this.currentAvatarId && content !== undefined) {
      try {
        const result = await updateMessageInSessionAction(this.currentAvatarId, messageId, content);
        if (!result.success) {
          console.error('Error updating message in database:', result.message);
        }
      } catch (error) {
        console.error('Error updating message in database:', error);
      }
    }

    this.notifySubscribers();
  }

  /**
   * Update a specific LiveMessage by messageId, appending to the content
   * @param messageId The ID of the message to update
   * @param content The new content for the message
   */
  public appendMessage(messageId: string, newContent: string): void {
    this.messages = this.messages.map(msg => 
      msg.id === messageId 
        ? { 
            ...msg, 
            content: msg.content + newContent,
          }
        : msg
    );
    this.notifySubscribers();
  }

  /**
   * Remove a specific LiveMessage by messageId and sync to database
   * @param messageId The ID of the message to remove
   */
  public async removeMessage(messageId: string): Promise<void> {
    this.messages = this.messages.filter(msg => msg.id !== messageId);

    // Sync to database if we have context
    if (this.currentUserEmail && this.currentAvatarId) {
      try {
        const result = await removeMessageFromSessionAction(this.currentAvatarId, messageId);
        if (!result.success) {
          console.error('Error removing message from database:', result.message);
        }
      } catch (error) {
        console.error('Error removing message from database:', error);
      }
    }

    this.notifySubscribers();
  }

  /**
   * Find a specific LiveMessage by messageId
   * @param messageId The ID of the message to find
   * @returns The LiveMessage if found, undefined otherwise
   */
  public findMessage(messageId: string): LiveMessage | undefined {
    return this.messages.find(msg => msg.id === messageId);
  }

  /**
   * Get the last message in the current messages array
   * @returns The last LiveMessage if messages exist, undefined otherwise
   */
  public getLastMessage(): LiveMessage {
    return this.messages[this.messages.length - 1];
  }

  /**
   * Get the last message with an image
   * @returns The last LiveMessage with an image if found, undefined otherwise
   */
  public getLastMessageWithImage(): LiveMessage | undefined {
    // Search from the end to find the last message with a non-empty imageUrl
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const message = this.messages[i];
      if (message.imageUrl && message.imageUrl.trim() !== '') {
        return message;
      }
    }
    // Return undefined if no message with image is found
    return undefined;
  }

  /**
   * Load messages from ChatMessage array (replaces current messages)
   * @param chatMessages Array of ChatMessages to load
   */
  public loadFromChatMessages(chatMessages: ChatMessage[]): void {
    this.messages = chatMessages.map(constructLiveMessageFromChatMessage);
    this.notifySubscribers();
  }
}

// Export singleton instance for easy importing
export const liveMessageDataHub = LiveMessageDataHub.getInstance(); 